<template>
  <!-- Auto-Import eingeplant: Restzeit + Stop (Task geht dann zu „Manuell") -->
  <div v-if="scheduled" class="autoImport" :title="'Wird automatisch importiert um ' + timeLabel + ' Uhr'">
    <v-icon x-small color="white" class="mr-1">mdi-timer-outline</v-icon>
    <span>{{ label }}</span>
    <button type="button" class="autoImport__stop" title="Auto-Import stoppen, Task manuell importieren" :disabled="stopping" @click.stop="stop">
      <v-icon x-small color="white">mdi-stop-circle-outline</v-icon>
    </button>
  </div>
  <!-- Nicht automatisch: „Manuell" + kurzer Grund, Langfassung als Tooltip -->
  <v-tooltip v-else-if="manualReason" top max-width="320">
    <template v-slot:activator="{ on, attrs }">
      <v-chip small outlined class="ml-2 autoImport__manual" color="white" text-color="white" v-bind="attrs" v-on="on">
        <v-icon x-small left>mdi-hand-back-right-outline</v-icon>Manuell<span class="autoImport__why">· {{ manualShort }}</span>
      </v-chip>
    </template>
    <span>Kein Auto-Import: {{ manualReason }}</span>
  </v-tooltip>
</template>

<script>
import { doc, updateDoc } from "firebase/firestore";
import { auth, db } from "../../../../fb";
import { clock } from "@/clock";

/**
 * Auto-Import-Anzeige der Import-Karte. `task.autoImport` schreibt der Server
 * (firebase_aq/functions-upscaler/src/autoImport.ts): scheduled | stopped | skipped | failed.
 */
export default {
  name: "AutoImportCountdown",
  props: { task: { type: Object, required: true } },
  data() {
    return { stopping: false };
  },
  computed: {
    info() {
      return this.task.autoImport || null;
    },
    scheduled() {
      return !!this.info && this.info.state === "scheduled" && !!this.info.at;
    },
    /** Grund, warum der Task nicht automatisch importiert wird — auch ohne Server-Vermerk (Score, kein Job …) */
    manualReason() {
      const info = this.info;
      if (info) {
        if (info.state === "stopped") return "von Hand gestoppt";
        if (info.state === "skipped") return info.reason || "ausgeschlossen";
        if (info.state === "failed") return `Auto-Import fehlgeschlagen: ${info.reason || "unbekannt"}`;
      }
      const cd = this.task.customizationData || {};
      const up = cd.upscale || null;
      if (cd.upscaleError) return `Upscale fehlgeschlagen: ${cd.upscaleError}`;
      if (!up || !cd.upscaleJobId) return "kein Upscale-Job";
      const min = up.taskScore && up.taskScore.min !== null && up.taskScore.min !== undefined ? Number(up.taskScore.min) : null;
      const limit = Number(up.taskScoreLimit) || 85;
      if (up.passed !== true) {
        if (min === null) return "kein Score (Prüfung ohne Ergebnis)";
        return `Score ${min} unter ${limit}${up.taskScore && up.taskScore.limitedBy ? ` (${up.taskScore.limitedBy})` : ""}`;
      }
      if (!info) return "Auto-Import nicht eingeplant";
      return "";
    },
    /** Kurzform für den Chip */
    manualShort() {
      const r = this.manualReason;
      if (!r) return "";
      if (/^Score \d+ unter/.test(r)) return r.replace(/\s*\(.*\)$/, "");
      if (r.startsWith("kein Score")) return "kein Score";
      if (r.startsWith("kein Upscale-Job")) return "kein Upscale";
      if (r.startsWith("Upscale fehlgeschlagen")) return "Upscale-Fehler";
      if (r.startsWith("Auto-Import fehlgeschlagen")) return "Import-Fehler";
      if (r === "von Hand gestoppt") return "gestoppt";
      return r.length > 28 ? r.slice(0, 26) + "…" : r;
    },
    /** „Import in 30:04 Minuten" / „Import in 1:20 Stunden" */
    label() {
      const rest = Math.floor((this.info.at - clock.now) / 1000);
      if (rest <= 0) return "Import steht an";
      const pad = (n) => String(n).padStart(2, "0");
      if (rest < 3600) return `Import in ${Math.floor(rest / 60)}:${pad(rest % 60)} Minuten`;
      return `Import in ${Math.floor(rest / 3600)}:${pad(Math.floor((rest % 3600) / 60))} Stunden`;
    },
    timeLabel() {
      const d = new Date(this.info.at);
      const today = new Date().toDateString() === d.toDateString();
      const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
      return today ? time : `${d.toLocaleDateString([], { weekday: "short", day: "2-digit", month: "2-digit" })} ${time}`;
    },
  },
  methods: {
    async stop() {
      this.stopping = true;
      try {
        await updateDoc(doc(db, "shopifyTasks", this.task.uid), {
          autoImport: { state: "stopped", at: null, stoppedAt: Date.now(), stoppedBy: (auth.currentUser && auth.currentUser.uid) || null },
        });
      } finally {
        this.stopping = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.autoImport__manual {
  max-width: 220px;
}
.autoImport__why {
  margin-left: 4px;
  font-weight: 600;
  opacity: 0.85;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.autoImport {
  display: inline-flex;
  align-items: center;
  margin: 0 auto;
  padding: 3px 4px 3px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  &__stop {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 22px;
    height: 22px;
    margin-left: 6px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.18);
    cursor: pointer;

    &:hover {
      background: #c0392b;
    }
    &:disabled {
      opacity: 0.5;
      cursor: default;
    }
  }
}
</style>
